import { useNavegacao } from "@/lib/navegacao";
import { Pode } from "@/lib/permissoes";
import { useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Megaphone, MailWarning, CalendarClock, ArrowRight, CheckCircle2, Send, ExternalLink, Handshake } from "lucide-react";
import { PageHeader } from "@/components/layout/page-header";
import { estadoDaConsulta } from "@/components/data/estado-consulta";
import { useAuth } from "@/lib/auth-context";
import { useHomeProdutor } from "@/hooks/use-mercado";
import { ProdutorShell } from "@/components/mercado/produtor-shell";
import { QuadroPracaConectado } from "@/components/mercado/quadro-praca";
import { PendenciaItem } from "@/components/mercado/pendencia-item";
import { StatusChip } from "@/components/mercado/status-chip";
import { DialogReenviar } from "@/components/mercado/dialog-reenviar";
import { DialogRecebi } from "@/components/mercado/dialog-recebi";
import { AvisoNovoComprador } from "@/components/mercado/aviso-novo-comprador";
import { fmtMoedaBR, fmtDiaCurto, fmtKgBR } from "@/lib/formato-mercado";
import type { HomeProdutorDTO, PendenciaDTO } from "@shared/mercado";
import { OFERTAS_ATIVAS, type PendenciaOfertaDTO, type ResumoOfertaHome } from "@shared/mercado-ofertas";

/**
 * Início do Mercado (home v0): "o que preciso fazer, e quanto ainda tenho para
 * receber". Duas colunas INDEPENDENTES (`1.35fr 1fr`), cada uma empilhando os
 * próprios cards — nunca grade 2×2, porque a linha de baixo herdaria a altura da
 * de cima e sobraria vão. Celular: uma coluna, quadro → pendências → a receber →
 * ofertas (que é exatamente a ordem coluna esquerda + coluna direita).
 */
export default function ProdutorInicio() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const consulta = useHomeProdutor(user?.orgTipo !== "COMPRADOR");
  const { data } = consulta;
  const aguardando = estadoDaConsulta(consulta, { variante: "painel" });

  return (
    <ProdutorShell>
      <PageHeader
        titulo="Mercado"
        descricao={OFERTAS_ATIVAS
          ? "Preço da praça, suas ofertas e o que ainda tem para receber."
          : "Preço da praça e o que ainda tem para receber."}
        empilharQuandoEstreito
        acoesSecundarias={
          <Button variant="outline" onClick={() => setLocation("/mercado/cotacoes")} data-testid="button-ver-cotacoes">
            Ver cotações
          </Button>
        }
        acao={OFERTAS_ATIVAS ? (
          <Button onClick={() => setLocation("/mercado/ofertas/nova")} data-testid="button-ofertar-carga">
            <Megaphone className="h-4 w-4 mr-1.5" />Ofertar carga
          </Button>
        ) : undefined}
      />

      <AvisoNovoComprador />

      {aguardando || !data ? (
        aguardando
      ) : (
        <div className="grid gap-4 md:grid-cols-[1.35fr_1fr] md:items-start">
          <div className="space-y-4 min-w-0">
            <QuadroPracaConectado onHistorico={() => setLocation("/mercado/cotacoes")} />
            <CardPendencias pendencias={data.pendencias} ofertas={data.pendenciasOfertas} />
          </div>
          <div className="space-y-4 min-w-0">
            <CardReceber receber={data.receber} />
            {/* Com Ofertas desligada o servidor já devolve lista vazia; o card
                sai daqui para não deixar uma seção "Ofertas em andamento" sem
                nada dentro, que é pior do que não ter seção. */}
            {OFERTAS_ATIVAS && <CardOfertas ofertas={data.ofertas} />}
          </div>
        </div>
      )}
    </ProdutorShell>
  );
}

/** O número do romaneio leva à venda no Cacho — é sempre o próximo lugar aonde
 *  se quer ir a partir dele. Mesmo alvo e mesmo ícone do detalhe do corretor. */
function LinkVenda({ numero, vendaId }: { numero: string; vendaId: string }) {
  // `abrir`: o "Voltar" do detalhe da venda devolve para o Início do Mercado.
  const { abrir } = useNavegacao();
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 underline underline-offset-2 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
      onClick={() => abrir(`/cacho/vendas/${vendaId}`)}
      data-testid={`link-venda-${vendaId}`}
    >
      {numero}<ExternalLink className="h-3 w-3" />
    </button>
  );
}

function CardPendencias({ pendencias, ofertas }: { pendencias: PendenciaDTO[]; ofertas: PendenciaOfertaDTO[] }) {
  const [, setLocation] = useLocation();
  const [enviar, setEnviar] = useState<PendenciaDTO | null>(null);
  const [reenviar, setReenviar] = useState<PendenciaDTO | null>(null);
  const [recebi, setRecebi] = useState<PendenciaDTO | null>(null);

  return (
    <section className="rounded-lg border bg-card shadow-xs" data-testid="card-pendencias">
      <header className="flex items-center justify-between border-b px-3 py-2">
        <p className="text-sm font-semibold">Precisa de você</p>
        <span className="text-xs text-muted-foreground">
          {pendencias.length + ofertas.length === 0
            ? "tudo em dia"
            : `${pendencias.length + ofertas.length} ${pendencias.length + ofertas.length === 1 ? "item" : "itens"}`}
        </span>
      </header>
      {pendencias.length + ofertas.length === 0 ? (
        <div className="px-3 py-6 flex flex-col items-center text-center gap-1.5">
          <CheckCircle2 className="h-6 w-6 text-sucesso" />
          <p className="text-sm font-medium">Nada pendente</p>
          <p className="text-xs text-muted-foreground">Romaneios abertos e prazos aparecem aqui quando precisarem de um toque seu.</p>
        </div>
      ) : (
        <ul className="px-3 py-2 divide-y">
          {/* As ofertas vêm PRIMEIRO: "3 compradores querem sua Prata" é decisão
              de hoje, com dinheiro entrando; cobrar uma nota vencida espera cinco
              minutos. */}
          {ofertas.map(o => (
            <PendenciaItem
              key={`oferta-${o.ofertaId}`}
              icone={Megaphone}
              tom="atencao"
              titulo={
                <>
                  {o.interessados} {o.interessados === 1 ? "comprador quer" : "compradores querem"} sua{" "}
                  {o.variedade === "NANICA" ? "Nanica" : "Prata"}
                </>
              }
              descricao={<>carga de ~{fmtKgBR(o.volumeKg)} · {o.identificador}</>}
              acoes={
                <Button size="sm" onClick={() => setLocation("/mercado/ofertas")} data-testid={`button-pendencia-oferta-${o.ofertaId}`}>
                  Escolher
                </Button>
              }
              testId={`pendencia-oferta-${o.ofertaId}`}
            />
          ))}
          {pendencias.map(p => p.tipo === "NAO_FORMALIZADA" ? (
            <PendenciaItem
              key={`${p.tipo}-${p.compradorId}`}
              icone={Handshake}
              tom="atencao"
              titulo={
                p.quantidade === 1
                  ? <>Romaneio <LinkVenda numero={p.numero} vendaId={p.vendaId} /> ainda não foi formalizado com {p.compradorNome}</>
                  : <>{p.quantidade} romaneios do {p.compradorNome} ainda não foram formalizados</>
              }
              descricao={<>{fmtMoedaBR(p.valor)} — ele não vê nenhuma dessas notas</>}
              acoes={
                <Button variant="outline" size="sm" onClick={() => setLocation("/mercado/receber")} data-testid={`button-pendencia-formalizar-${p.compradorId}`}>
                  Ver em A receber
                </Button>
              }
              testId={`pendencia-formalizar-${p.compradorId}`}
            />
          ) : p.tipo === "NAO_ENVIADO" ? (
            <PendenciaItem
              key={`${p.tipo}-${p.compartilhamentoId}`}
              icone={Send}
              tom="atencao"
              titulo={<>Romaneio <LinkVenda numero={p.numero} vendaId={p.vendaId} /> formalizado e não enviado ao {p.compradorNome}</>}
              descricao={
                <>
                  {p.dias > 0 ? `formalizado há ${p.dias} ${p.dias === 1 ? "dia" : "dias"} · ` : ""}
                  {fmtMoedaBR(p.valor)} — ele ainda não sabe desta nota
                </>
              }
              acoes={
                <Button size="sm" onClick={() => setEnviar(p)} data-testid={`button-pendencia-enviar-${p.compartilhamentoId}`}>
                  <Send className="h-4 w-4 mr-1.5" />Enviar pelo WhatsApp
                </Button>
              }
              testId={`pendencia-${p.compartilhamentoId}`}
            />
          ) : p.tipo === "NAO_ABRIU" ? (
            <PendenciaItem
              key={`${p.tipo}-${p.compartilhamentoId}`}
              icone={MailWarning}
              tom="atencao"
              titulo={<>{p.compradorNome} ainda não abriu o romaneio <LinkVenda numero={p.numero} vendaId={p.vendaId} /></>}
              descricao={<>enviado há {p.dias} dias · {fmtMoedaBR(p.valor)}</>}
              acoes={
                <Button variant="outline" size="sm" onClick={() => setReenviar(p)} data-testid={`button-pendencia-reenviar-${p.compartilhamentoId}`}>
                  Reenviar
                </Button>
              }
              testId={`pendencia-${p.compartilhamentoId}`}
            />
          ) : (
            <PendenciaItem
              key={`${p.tipo}-${p.compartilhamentoId}`}
              icone={CalendarClock}
              tom={p.dias < 0 ? "erro" : "atencao"}
              titulo={
                <>
                  <LinkVenda numero={p.numero} vendaId={p.vendaId} /> · {p.compradorNome}{" "}
                  {p.dias < 0
                    ? `venceu há ${Math.abs(p.dias)} ${Math.abs(p.dias) === 1 ? "dia" : "dias"}`
                    : p.dias === 0 ? "vence hoje" : `vence ${fmtDiaCurto(p.vencimento)}`}
                </>
              }
              descricao={fmtMoedaBR(p.valor)}
              acoes={
                <Pode cap="mercado.receber"><Button size="sm" onClick={() => setRecebi(p)} data-testid={`button-pendencia-recebi-${p.compartilhamentoId}`}>
                  Recebi
                </Button></Pode>
              }
              testId={`pendencia-${p.compartilhamentoId}`}
            />
          ))}
        </ul>
      )}

      {enviar && enviar.compartilhamentoId && (
        <DialogReenviar
          compartilhamentoId={enviar.compartilhamentoId}
          titulo={`Enviar o romaneio ${enviar.numero}`}
          descricao={`Para ${enviar.compradorNome}, pelo seu WhatsApp. É este link que cria o acesso dele.`}
          primeiroEnvio
          onFechar={() => setEnviar(null)}
        />
      )}
      {reenviar && reenviar.compartilhamentoId && (
        <DialogReenviar
          compartilhamentoId={reenviar.compartilhamentoId}
          titulo={`Reenviar o romaneio ${reenviar.numero}`}
          descricao={`Para ${reenviar.compradorNome}, pelo seu WhatsApp.`}
          onFechar={() => setReenviar(null)}
        />
      )}
      {recebi && (
        <DialogRecebi
          romaneios={[{ vendaId: recebi.vendaId, numero: recebi.numero, comprador: recebi.compradorNome, valor: recebi.valor, dataVenda: recebi.dataVenda }]}
          inicialId={recebi.vendaId}
          onFechar={() => setRecebi(null)}
        />
      )}
    </section>
  );
}

function CardReceber({ receber }: { receber: HomeProdutorDTO["receber"] }) {
  const [, setLocation] = useLocation();
  const maior = receber.porComprador.reduce((m, c) => Math.max(m, c.valor), 0);

  return (
    <section className="rounded-lg border bg-card shadow-xs" data-testid="card-receber">
      <header className="flex items-center justify-between border-b px-3 py-2">
        <p className="text-sm font-semibold">A receber</p>
        <button type="button" className="text-xs text-muted-foreground inline-flex items-center gap-1 hover:text-foreground" onClick={() => setLocation("/mercado/receber")} data-testid="link-ver-receber">
          Agenda <ArrowRight className="h-3 w-3" />
        </button>
      </header>
      <div className="p-3 space-y-3">
        <div>
          <p className="font-mono text-2xl font-semibold tabular-nums whitespace-nowrap" data-testid="texto-receber-total">{fmtMoedaBR(receber.total)}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {receber.romaneios} {receber.romaneios === 1 ? "romaneio" : "romaneios"} · {receber.compradores} {receber.compradores === 1 ? "comprador" : "compradores"}
          </p>
          {receber.venceSemana && (
            <div className="mt-2">
              <StatusChip kind="warn" testId="chip-vence-semana">
                {receber.venceSemana.qtd} {receber.venceSemana.qtd === 1 ? "vence" : "vencem"} esta semana · {fmtMoedaBR(receber.venceSemana.valor)}
              </StatusChip>
            </div>
          )}
        </div>

        {receber.porComprador.length > 0 && (
          <ul className="space-y-2">
            {receber.porComprador.map(c => (
              <li key={c.compradorId} className="space-y-1" data-testid={`receber-comprador-${c.compradorId}`}>
                <div className="flex items-center justify-between gap-2 text-xs">
                  <span className="font-medium truncate">{c.nome}</span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span className="font-mono tabular-nums font-semibold">{fmtMoedaBR(c.valor)}</span>
                    {c.status === "VENCIDO" && <StatusChip kind="danger">vencido</StatusChip>}
                    {c.status === "VENCE_SEMANA" && <StatusChip kind="warn">esta semana</StatusChip>}
                    {c.status === "A_PAGAR" && <StatusChip kind="aberta">A pagar</StatusChip>}
                  </span>
                </div>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full rounded-full bg-primary/70" style={{ width: `${maior > 0 ? (c.valor / maior) * 100 : 0}%` }} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <footer className="border-t bg-muted/40 px-3 py-2 text-xs text-muted-foreground flex items-center justify-between">
        <span>Recebido no mês</span>
        <span className="font-mono tabular-nums font-semibold text-foreground" data-testid="texto-recebido-mes">{fmtMoedaBR(receber.recebidoMes)}</span>
      </footer>
    </section>
  );
}

function CardOfertas({ ofertas }: { ofertas: ResumoOfertaHome[] }) {
  const [, setLocation] = useLocation();

  return (
    <section className="rounded-lg border bg-card shadow-xs" data-testid="card-ofertas">
      <header className="flex items-center justify-between border-b px-3 py-2">
        <p className="text-sm font-semibold">Ofertas em andamento</p>
        {ofertas.length > 0 && (
          <button
            type="button"
            className="text-xs text-muted-foreground inline-flex items-center gap-1 hover:text-foreground"
            onClick={() => setLocation("/mercado/ofertas")}
            data-testid="link-ver-ofertas"
          >
            Ver todas <ArrowRight className="h-3 w-3" />
          </button>
        )}
      </header>
      {ofertas.length === 0 ? (
        <div className="px-3 py-6 flex flex-col items-center text-center gap-2">
          <Megaphone className="h-6 w-6 text-muted-foreground" />
          <p className="text-sm font-medium">Nenhuma oferta na praça</p>
          <p className="text-xs text-muted-foreground max-w-xs">
            Coloque uma carga na praça: a chamada vai anônima, o preço é o da praça no dia da carga, e os compradores respondem se têm interesse.
          </p>
          <Button variant="outline" size="sm" className="mt-1" onClick={() => setLocation("/mercado/ofertas/nova")}>
            Ofertar carga
          </Button>
        </div>
      ) : (
        <ul className="divide-y">
          {ofertas.map(o => (
            <li key={o.id} className="px-3 py-2.5 space-y-0.5" data-testid={`home-oferta-${o.id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium truncate">
                  {o.variedade === "NANICA" ? "Nanica" : "Prata"} · ~{fmtKgBR(o.volumeKg)}
                </span>
                {/* PARCIAL saiu com a 059: sem kg no fechamento, a carga está na
                    praça ou fechada — e a home só lista as que estão na praça. */}
                <StatusChip kind="info">Na praça</StatusChip>
              </div>
              <p className="text-[11px] text-muted-foreground">
                {o.interessados > 0
                  ? <>na praça desde {fmtDiaCurto((o.publicadaEm ?? "").substring(0, 10) || o.disponivelEm)} · {o.interessados} {o.interessados === 1 ? "interessado" : "interessados"}</>
                  : <>na praça · disponível {fmtDiaCurto(o.disponivelEm)}</>}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
